import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { register, searchMembersForRegister } from '../services/api';
import { FiSearch, FiCheck, FiUser, FiPlus } from 'react-icons/fi';
import PhoneInput from '../components/PhoneInput';

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: '', username: '', password: '', confirm: '', phone: '' });
  const [type, setType] = useState(''); // '' | 'existing' | 'new'
  const [memberQuery, setMemberQuery] = useState('');
  const [memberSuggest, setMemberSuggest] = useState([]);
  const [pickedMember, setPickedMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));
  const setNum = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value.replace(/\D/g, '') }));

  const goStep2 = (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.username || !form.password) return setError(t('register.fieldsRequired'));
    if (!/^\d{4,}$/.test(form.password)) return setError(t('register.passwordRule'));
    if (form.password !== form.confirm) return setError(t('register.passwordsDoNotMatch'));
    if (!/^[a-zA-Z0-9_]+$/.test(form.username)) return setError(t('register.usernameRule'));
    setStep(2);
  };

  const lookupMembers = async (q) => {
    setMemberQuery(q);
    if (q.trim().length < 2) { setMemberSuggest([]); return; }
    try {
      const r = await searchMembersForRegister(q);
      setMemberSuggest(Array.isArray(r.data) ? r.data : []);
    } catch { setMemberSuggest([]); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.phone || !/^\+260\d{9}$/.test(form.phone)) return setError(t('register.phoneRequired'));
    if (type === 'existing' && !pickedMember) return setError(t('register.pickMember'));
    setLoading(true);
    try {
      await register({
        name: form.name,
        username: form.username,
        password: form.password,
        phone: form.phone,
        registration_type: type,
        requested_member_id: type === 'existing' ? pickedMember.id : null,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || t('register.registrationFailed'));
    } finally { setLoading(false); }
  };

  const inp = { width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' };

  if (success) {
    return (
      <div className="login-page">
        <div className="login-card" style={{ maxWidth: 460, textAlign: 'center' }}>
          <div className="login-logo" style={{ marginBottom: 6 }}>
            <img src="/logo.png" alt="St. Michael Orthodox Church" style={{ width: 100, height: 100, objectFit: 'contain' }} />
          </div>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#16a34a', marginBottom: 8 }}>{t('register.accountSubmitted')}</h3>
          <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 24, lineHeight: 1.6 }}>
            {t('register.pendingNote')}<br />
            {t('register.loginAfterApproval')}
          </p>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => navigate('/login')}>
            {t('register.backToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: 480 }}>
        <div className="login-logo" style={{ marginBottom: 6 }}>
          <img src="/logo.png" alt="St. Michael Orthodox Church" style={{ width: 90, height: 90, objectFit: 'contain' }} />
        </div>
        <h2 className="login-title" style={{ fontSize: 18 }}>{t('register.title')}</h2>
        <p className="login-sub" style={{ marginBottom: 16 }}>{t('register.step', { current: step, total: 2 })}</p>

        {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

        {/* Step indicators */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          <div style={{ flex: 1, height: 4, borderRadius: 2, background: step >= 1 ? '#4f46e5' : '#e5e7eb' }} />
          <div style={{ flex: 1, height: 4, borderRadius: 2, background: step >= 2 ? '#4f46e5' : '#e5e7eb' }} />
        </div>

        {step === 1 && (
          <form onSubmit={goStep2}>
            <div className="form-group">
              <label>{t('register.fullName')} <span className="required">*</span></label>
              <input style={inp} value={form.name} onChange={set('name')} autoFocus />
            </div>
            <div className="form-group">
              <label>{t('register.username')} <span className="required">*</span></label>
              <input style={inp} value={form.username} onChange={set('username')} />
              <small style={{ color: '#6b7280', fontSize: 11 }}>{t('register.usernameHint')}</small>
            </div>
            <div className="form-group">
              <label>{t('register.password')} <span className="required">*</span></label>
              <input style={inp} type="password" inputMode="numeric" pattern="[0-9]*" maxLength={20}
                value={form.password} onChange={setNum('password')} placeholder={t('register.passwordPlaceholder')} />
            </div>
            <div className="form-group">
              <label>{t('register.confirmPassword')} <span className="required">*</span></label>
              <input style={inp} type="password" inputMode="numeric" pattern="[0-9]*" maxLength={20}
                value={form.confirm} onChange={setNum('confirm')} />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>
              {t('register.next')}
            </button>
            <div style={{ textAlign: 'center', marginTop: 14, fontSize: 13, color: '#6b7280' }}>
              {t('register.alreadyHaveAccount')}{' '}
              <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate('/login')}>{t('register.signIn')}</span>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submit}>
            {!type && (
              <>
                <p style={{ fontSize: 13, color: '#374151', marginBottom: 14 }}>
                  {t('register.memberOrNewQuestion')}
                </p>
                <button type="button" className="btn btn-secondary" style={{ width: '100%', marginBottom: 10, justifyContent: 'flex-start', padding: '14px 16px' }}
                  onClick={() => setType('existing')}>
                  <FiUser /> &nbsp; {t('register.alreadyMember')}
                </button>
                <button type="button" className="btn btn-secondary" style={{ width: '100%', justifyContent: 'flex-start', padding: '14px 16px' }}
                  onClick={() => setType('new')}>
                  <FiPlus /> &nbsp; {t('register.newToChurch')}
                </button>
              </>
            )}

            {type === 'existing' && (
              <>
                <div className="form-group">
                  <label>{t('register.findInDirectory')} <span className="required">*</span></label>
                  {pickedMember ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#eef2ff', border: '1px solid #a5b4fc', borderRadius: 8 }}>
                      <div>
                        <strong style={{ color: '#4f46e5' }}>{pickedMember.first_name} {pickedMember.middle_name || ''} {pickedMember.last_name}</strong>
                        {pickedMember.card_number && <span style={{ marginLeft: 8, fontFamily: 'monospace', fontSize: 11, color: '#6b7280' }}>{pickedMember.card_number}</span>}
                      </div>
                      <button type="button" className="btn-icon" onClick={() => { setPickedMember(null); setMemberQuery(''); }}><FiSearch /></button>
                    </div>
                  ) : (
                    <>
                      <div style={{ position: 'relative' }}>
                        <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                        <input style={{ ...inp, paddingLeft: 38 }} value={memberQuery}
                          onChange={e => lookupMembers(e.target.value)} placeholder={t('register.typeYourName')} />
                      </div>
                      {memberSuggest.length > 0 && (
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 6, maxHeight: 220, overflowY: 'auto', background: '#fff' }}>
                          {memberSuggest.map(m => (
                            <div key={m.id} onClick={() => { setPickedMember(m); setMemberSuggest([]); }}
                              style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{m.first_name} {m.middle_name || ''} {m.last_name}</div>
                              {m.card_number && <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>{m.card_number}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="form-group">
                  <label>{t('register.phone')} <span className="required">*</span></label>
                  <PhoneInput value={form.phone} onChange={v => setForm(p => ({ ...p, phone: v }))} placeholder="0977123456" required />
                  <small style={{ color: '#6b7280', fontSize: 11 }}>{t('register.phoneHint')}</small>
                </div>
              </>
            )}

            {type === 'new' && (
              <>
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#92400e', marginBottom: 14 }}>
                  {t('register.newWelcomeNote')}
                </div>
                <div className="form-group">
                  <label>{t('register.phone')} <span className="required">*</span></label>
                  <PhoneInput value={form.phone} onChange={v => setForm(p => ({ ...p, phone: v }))} placeholder="0977123456" required />
                </div>
              </>
            )}

            {type && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={() => { setType(''); setPickedMember(null); }}>
                  {t('register.change')}
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} disabled={loading}>
                  {loading ? t('register.submitting') : <><FiCheck /> &nbsp; {t('register.submit')}</>}
                </button>
              </div>
            )}

            <button type="button" className="btn btn-secondary" style={{ width: '100%', marginTop: 12, justifyContent: 'center' }} onClick={() => setStep(1)}>
              {t('register.back')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Register;
